import type { MDXComponents } from "mdx/types";
import { ChapterTitle } from "@/components/ChapterTitle";
import { SubsectionTitle } from "@/components/SubsectionTitle";
import { ArticleTitle } from "@/components/ArticleTitle";
import { LargeQuote } from "@/components/LargeQuote";
import { TwoColumn, TwoColumnItem } from "@/components/TwoColumn";
import { ThreeColumn, ThreeColumnItem } from "@/components/ThreeColumn";
import { InsetColumn } from "@/components/InsetColumn";
import { PageSection } from "@/components/PageSection";
import { MoreInfoPopover, MoreInfoContent } from "@/components/MoreInfoPopover";
import { Quiz } from "@/components/Quiz";
import { ExternalLink } from "@/components/ExternalLink";
import { Video } from "@/components/Video";
import { Footer } from "@/components/Footer";
import { Typography, Box } from "@mui/material";
import Image from "next/image";

export function useMDXComponents(components: MDXComponents): MDXComponents {
  return {
    img: (props: React.ImgHTMLAttributes<HTMLImageElement>) => {
      const { src, alt = "" } = props;
      if (!src || typeof src !== "string") return null;

      // For relative paths (chapter images), they need to be resolved from public
      // MDX images with relative paths like ./images/foo.svg need special handling
      const imageSrc = src.startsWith("./")
        ? src.replace("./", "/")
        : src.startsWith("/")
          ? src
          : `/${src}`;

      // Check if the image is a gif (animated images should not be optimized)
      const isGif = imageSrc.toLowerCase().endsWith(".gif");

      return (
        <Image
          src={imageSrc}
          alt={alt}
          width={800}
          height={600}
          unoptimized={isGif}
          style={{
            width: "100%",
            height: "auto",
            maxWidth: "100%",
          }}
        />
      );
    },
    h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h1" component="h1" {...props} />
    ),
    h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h2" component="h2" {...props} />
    ),
    h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h3" component="h3" {...props} />
    ),
    h4: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h4" component="h4" {...props} />
    ),
    h5: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h5" component="h5" {...props} />
    ),
    h6: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography variant="h6" component="h6" {...props} />
    ),
    p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
      <Typography variant="body1" component="p" {...props} />
    ),
    ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
      <Box
        component="ol"
        sx={{
          paddingLeft: "2rem",
          marginLeft: "-0.5rem",
          listStylePosition: "outside",
          "& > li": {
            paddingLeft: "0.5rem",
            marginBottom: "0.75rem",
          },
          "& > li::marker": {
            fontSize: "1.2em",
          },
        }}
        {...props}
      />
    ),
    ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
      <Box
        component="ul"
        sx={{
          paddingLeft: 0,
          marginLeft: -3,
          listStylePosition: "outside",
          "& > li": {
            paddingLeft: "0.5rem",
            marginBottom: "0.5rem",
          },
          "& > li::marker": {
            fontSize: "1.3em",
          },
          "& li > ul": {
            marginTop: "0.5rem",
            marginBottom: 0,
            marginLeft: "0",
            paddingLeft: "1.5rem",
            listStyleType: "disc",
          },
          "& li > ul > li": {
            paddingLeft: "0.5rem",
            marginBottom: "0.5rem",
          },
          "& li > ul > li::marker": {
            fontSize: "1.3em",
          },
        }}
        {...props}
      />
    ),
    ChapterTitle,
    SubsectionTitle,
    ArticleTitle,
    LargeQuote,
    TwoColumn,
    TwoColumnItem,
    ThreeColumn,
    ThreeColumnItem,
    InsetColumn,
    PageSection,
    MoreInfoPopover,
    MoreInfoContent,
    Quiz,
    ExternalLink,
    Video,
    Footer,
    ...components,
  };
}
